import cx from 'classnames'
import Styles from './Form.module.scss'
import { FormProps } from './Types'
import { object, string } from 'yup'
import { Input } from 'almanac-ui/TextInputField/Input'
import { IconButton } from 'components/IconButton/IconButton'
import { ArrowRight } from 'blaze-ui/icons'
import { Formik } from 'formik'
import { Chip } from './components/Chip'
import { ChipMenu } from './components/ChipMenu'
import { capitalize } from 'lodash'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

export const ExamplePrompts = [
  'A sweater that says “Blaze.ai”',
  'Boutique clothing store spring sale',
  'Fitness studio launching online classes',
  'Local bakery showcasing artisan bread',
]

const schema = object({
  topic: string().required().min(5),
  image_type: string().required().oneOf(['illustration', 'photo']).default('photo'),
  image_aspect_ratio: string().required().oneOf(['square', 'landscape', 'portrait']).default('square'),
  priority: string().required().oneOf(['speed', 'quality']).default('speed'),
})

export const IMAGE_TYPES = ['photo', 'illustration']
export const ASPECT_RATIOS = ['square', 'landscape', 'portrait']
export const PRIORITIES = ['speed', 'quality']

export const Form = ({
  onSubmit,
  showExamples = true,
  showChips = true,
  placeholder = 'Describe an image',
  initialValues = {},
}: FormProps) => {
  const isGptImageOneEnabled = useFlipperFlag('gpt_image_one')

  return (
    <Formik
      initialValues={{ ...schema.cast(schema.getDefault(), { assert: false }), ...initialValues }}
      validationSchema={schema}
      onSubmit={onSubmit}
    >
      {({
        values: { topic, image_type, image_aspect_ratio, priority },
        setFieldValue,
        submitForm,
        dirty,
        errors,
        isValid,
        isSubmitting,
      }) => (
        <div className={cx(Styles.formContainer, { [Styles.fullScreen]: showExamples })}>
          {showExamples && (
            <div className={Styles.formInfo}>
              <div className={Styles.header}>
                <div className={Styles.description}>Describe the image you want to generate—or try an example</div>
              </div>
              <div className={Styles.examplePrompts}>
                {ExamplePrompts.map((prompt) => (
                  <Chip key={prompt} onPress={() => setFieldValue('topic', prompt)}>
                    <span className={Styles.promptLabel}>{prompt}</span>
                  </Chip>
                ))}
              </div>
            </div>
          )}
          <div className={Styles.inputContainer}>
            <div className={Styles.promptInput}>
              <Input
                variant="grey"
                size="xl"
                name="Prompt"
                placeholder={placeholder}
                value={topic || ''}
                onChange={(e) => setFieldValue('topic', e.target.value)}
                isValid={!dirty || !errors.topic}
                autoComplete="off"
              />
              <IconButton
                size="xl"
                variant="primary"
                icon={ArrowRight}
                onPress={async () => {
                  await submitForm()
                  setFieldValue('topic', '')
                }}
                isDisabled={isSubmitting || !isValid}
                data-testid="submit-button"
              />
            </div>
            {showChips && (
              <div className={Styles.options}>
                <ChipMenu
                  type="Size"
                  label={capitalize(image_aspect_ratio)}
                  selectedOption={image_aspect_ratio}
                  options={ASPECT_RATIOS}
                  onChange={(option) => setFieldValue('image_aspect_ratio', option)}
                />
                <ChipMenu
                  type="Style"
                  label={capitalize(image_type)}
                  selectedOption={image_type}
                  options={IMAGE_TYPES}
                  onChange={(option) => setFieldValue('image_type', option)}
                />
                {isGptImageOneEnabled && (
                  <ChipMenu
                    type="Priority"
                    label={capitalize(priority)}
                    selectedOption={priority}
                    options={PRIORITIES}
                    onChange={(option) => setFieldValue('priority', option)}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </Formik>
  )
}
